import * as yup from 'yup';
import { RendererType } from '../data-models/field-definition';
import { dateField, integerField, percentField, pricePerShareField, usdField } from './common-schema-defs';

export const SecurityReportSchema = {
  base() {
    return {
      companyId: yup.number().label('Company'),
      currentHolding: yup.string().label('Position'),
      discountRate: percentField().nullable().label('Discount %'),
      escrow: usdField(),
      fmv: usdField().label('FMV'),
      fundId: yup.number().label('Fund'),
      initialHolding: yup.string(),
      initialInvestmentDate: dateField(),
      investmentAmount: usdField(),
      latestEventDate: dateField(),
      pps: pricePerShareField().label('PPS'),
      realizedValue: usdField(),
      restructureId: yup.string(),
      sharesHeld: integerField(),
      unrealizedCapitalizedCost: usdField().label('Unrealized Capitalized Cost'),
      valuationCap: usdField().nullable().label('Valuation Cap'),
    };
  },
  model() {
    return yup.object().shape(SecurityReportSchema.base());
  },
  gridSchema() {
    const {
      companyId,
      currentHolding,
      investmentAmount,
      fmv,
      realizedValue,
      escrow,
      sharesHeld,
      unrealizedCapitalizedCost,
      fundId,
      ...rest
    } = SecurityReportSchema.base();
    return yup.object().shape({
      ...rest,
      companyId: companyId.gridMeta({
        renderer: RendererType.companyId,
        enableRowGroup: true,
      }),
      currentHolding: currentHolding.gridMeta({
        enableRowGroup: true,
      }),
      investmentAmount: investmentAmount.gridMeta({
        aggFunc: 'sum',
      }),
      fmv: fmv.gridMeta({
        aggFunc: 'sum',
      }),
      fundId: fundId.gridMeta({
        renderer: RendererType.id,
        formatter: 'fund',
      }),
      realizedValue: realizedValue.gridMeta({
        aggFunc: 'sum',
      }),
      escrow: escrow.gridMeta({
        aggFunc: 'sum',
      }),
      sharesHeld: sharesHeld.gridMeta({
        aggFunc: 'sum',
      }),
      unrealizedCapitalizedCost: unrealizedCapitalizedCost.gridMeta({
        aggFunc: 'sum',
      }),
    });
  },
};

export type SecurityReport = yup.InferType<ReturnType<typeof SecurityReportSchema.model>>;

// Attached by the API for security custom fields; kept off the yup schema so the grid schema
// doesn't turn them into columns. securityId is null while the firm has no security fields.
export type SecurityReportRow = SecurityReport & {
  securityId?: number | null;
  customData?: Record<string, unknown>;
};
